import { useEffect, useState } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  fetchMarketData,
  fetchAllMarketData,
  getFinnhubSubscriptions,
  subscribeFinnhub,
  unsubscribeFinnhub,
  getIntradayBars,
  getFinnhubQuote,
  type IntradayBar,
  type FinnhubQuote,
} from "../api/client";
import { format } from "date-fns";
import WsHealthMonitor from "../components/WsHealthMonitor";

// --- Candlestick shape ---
interface CandleProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  open?: number | null;
  high?: number | null;
  low?: number | null;
  close?: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxis?: { scale: (v: number) => number; [key: string]: any };
  [key: string]: unknown;
}

const CandlestickBar = (props: CandleProps) => {
  const { x = 0, width = 0, open, high, low, close, yAxis } = props;
  if (open == null || high == null || low == null || close == null || !yAxis?.scale) return null;
  const isUp = close >= open;
  const color = isUp ? "#16a34a" : "#dc2626";
  const highY = yAxis.scale(high);
  const lowY = yAxis.scale(low);
  const openY = yAxis.scale(open);
  const closeY = yAxis.scale(close);
  const bodyTop = Math.min(openY, closeY);
  const bodyBottom = Math.max(openY, closeY);
  const bodyHeight = Math.max(bodyBottom - bodyTop, 1);
  const cx = x + width / 2;
  const candleW = Math.max(width - 2, 1);
  // If wick and body have zero visual height (e.g. flat bar), render a marker dot
  if (Math.abs(highY - lowY) < 1) {
    return <circle cx={cx} cy={highY} r={3} fill={color} />;
  }
  return (
    <g>
      <line x1={cx} x2={cx} y1={highY} y2={lowY} stroke={color} strokeWidth={1} />
      <rect x={x + 1} y={bodyTop} width={candleW} height={bodyHeight} fill={color} />
    </g>
  );
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20, marginBottom: 20 }}>
      <h3 style={{ margin: "0 0 12px", color: "#334155", fontSize: 15 }}>{title}</h3>
      {children}
    </div>
  );
}

function btnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "8px 16px",
    background: disabled ? "#e2e8f0" : "#3b82f6",
    color: disabled ? "#94a3b8" : "#fff",
    border: "none",
    borderRadius: 6,
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 14,
    fontWeight: 500,
  };
}

export default function DataPage() {
  // ── Alpha Vantage state ──────────────────────────────────────────────────
  const [marketSymbol, setMarketSymbol] = useState("");
  const [fetchingMarket, setFetchingMarket] = useState(false);
  const [marketResult, setMarketResult] = useState<string | null>(null);
  const [marketError, setMarketError] = useState<string | null>(null);

  const [fetchingAllMarket, setFetchingAllMarket] = useState(false);
  const [allMarketResult, setAllMarketResult] = useState<string | null>(null);
  const [allMarketError, setAllMarketError] = useState<string | null>(null);

  const handleFetchMarket = async () => {
    if (!marketSymbol) return;
    setFetchingMarket(true);
    setMarketResult(null);
    setMarketError(null);
    try {
      const res = await fetchMarketData(marketSymbol);
      const d = res.data as { rows_upserted: number };
      setMarketResult(`✅ ${d.rows_upserted} rows upserted for ${marketSymbol.toUpperCase()}.`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ?? (err as { message?: string })?.message ?? "Unknown error";
      setMarketError(`❌ ${msg}`);
    } finally {
      setFetchingMarket(false);
    }
  };

  const handleFetchAllMarket = async () => {
    setFetchingAllMarket(true);
    setAllMarketResult(null);
    setAllMarketError(null);
    try {
      const res = await fetchAllMarketData();
      const { fetched, errors } = res.data;
      const total = fetched.reduce((sum: number, r: { rows_upserted: number }) => sum + r.rows_upserted, 0);
      let msg = `✅ ${fetched.length} symbols fetched, ${total} rows upserted.`;
      if (errors.length > 0) msg += ` ⚠️ ${errors.length} errors: ${errors.map((e: { symbol: string }) => e.symbol).join(", ")}.`;
      setAllMarketResult(msg);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ?? (err as { message?: string })?.message ?? "Unknown error";
      setAllMarketError(`❌ ${msg}`);
    } finally {
      setFetchingAllMarket(false);
    }
  };

  // ── Finnhub / Intraday state ─────────────────────────────────────────────
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  const [newSymbol, setNewSymbol] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [bars, setBars] = useState<IntradayBar[]>([]);
  const [quote, setQuote] = useState<FinnhubQuote | null>(null);
  const [loadingBars, setLoadingBars] = useState(false);
  const [intradayError, setIntradayError] = useState<string | null>(null);

  const loadSubscriptions = () => {
    getFinnhubSubscriptions()
      .then((res) => setSubscriptions(res.data.symbols))
      .catch(() => {});
  };

  useEffect(() => { loadSubscriptions(); }, []);

  const handleSubscribe = async () => {
    const sym = newSymbol.trim().toUpperCase();
    if (!sym) return;
    try {
      await subscribeFinnhub(sym);
      setNewSymbol("");
      loadSubscriptions();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setIntradayError(detail ?? "Failed to subscribe. Check FINNHUB_API_KEY.");
    }
  };

  const handleUnsubscribe = async (sym: string) => {
    await unsubscribeFinnhub(sym);
    if (selectedSymbol === sym) setSelectedSymbol(null);
    loadSubscriptions();
  };

  const handleSelectSymbol = async (sym: string) => {
    setSelectedSymbol(sym);
    setLoadingBars(true);
    setIntradayError(null);
    try {
      const [barsRes, quoteRes] = await Promise.all([
        getIntradayBars({ symbol: sym, limit: 500 }),
        getFinnhubQuote(sym).catch(() => ({ data: null })),
      ]);
      setBars([...barsRes.data].reverse());
      setQuote(quoteRes.data);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setIntradayError(detail ?? "Failed to load data");
    } finally {
      setLoadingBars(false);
    }
  };

  const formatted = bars.map((b) => ({
    ...b,
    timeLabel: format(new Date(b.timestamp), "HH:mm"),
    open: b.open != null ? Number(b.open) : null,
    high: b.high != null ? Number(b.high) : null,
    low: b.low != null ? Number(b.low) : null,
    close: b.close != null ? Number(b.close) : null,
    volume: b.volume != null ? Number(b.volume) : null,
  }));

  const validBars = formatted.filter((b) => b.open != null && b.high != null && b.low != null && b.close != null);
  const yMin = validBars.length ? Math.min(...validBars.map((b) => b.low!)) : 0;
  const yMax = validBars.length ? Math.max(...validBars.map((b) => b.high!)) : 1;
  const yPad = (yMax - yMin) * 0.02 || 0.01;
  const yDomain: [number, number] = [yMin - yPad, yMax + yPad];

  return (
    <div>
      <h2 style={{ margin: "0 0 20px", color: "#1e293b" }}>Market Data &amp; Intraday</h2>

      {/* ── Alpha Vantage ─────────────────────────────────────────── */}
      <h3 style={{ margin: "0 0 12px", color: "#475569", fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Alpha Vantage — Daily OHLCV
      </h3>

      <Section title="Fetch by Symbol">
        <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 12px" }}>
          Fetch daily OHLCV data for a single symbol from Alpha Vantage.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            placeholder="Symbol (e.g. AAPL)"
            value={marketSymbol}
            onChange={(e) => setMarketSymbol(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleFetchMarket()}
            style={{ padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14, width: 160 }}
          />
          <button onClick={handleFetchMarket} disabled={!marketSymbol || fetchingMarket} style={btnStyle(!marketSymbol || fetchingMarket)}>
            {fetchingMarket ? "Fetching…" : "Fetch Symbol"}
          </button>
        </div>
        {marketResult && <p style={{ color: "#16a34a", marginTop: 8, fontSize: 13 }}>{marketResult}</p>}
        {marketError && <p style={{ color: "#dc2626", marginTop: 8, fontSize: 13 }}>{marketError}</p>}
      </Section>

      <Section title="Fetch All Symbols">
        <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 12px" }}>
          Refresh daily data for <strong>all known non-currency symbols</strong> at once. May take a while due to rate limits.
        </p>
        <button onClick={handleFetchAllMarket} disabled={fetchingAllMarket} style={btnStyle(fetchingAllMarket)}>
          {fetchingAllMarket ? "Fetching all…" : "Fetch All Symbols"}
        </button>
        {allMarketResult && <p style={{ color: "#16a34a", marginTop: 8, fontSize: 13 }}>{allMarketResult}</p>}
        {allMarketError && <p style={{ color: "#dc2626", marginTop: 8, fontSize: 13 }}>{allMarketError}</p>}
      </Section>

      {/* ── Finnhub Intraday ──────────────────────────────────────── */}
      <h3 style={{ margin: "24px 0 12px", color: "#475569", fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Finnhub — Real-time Intraday
      </h3>

      {/* WebSocket health monitor */}
      <WsHealthMonitor />

      <Section title="WebSocket Subscriptions">
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>
          Subscribe to symbols to capture real-time trades aggregated into 5-minute bars (configurable via <code>INTRADAY_BAR_MINUTES</code>). Requires{" "}
          <code>FINNHUB_API_KEY</code> in the backend.
        </p>

        {intradayError && (
          <div style={{ color: "#dc2626", marginBottom: 12, fontSize: 13 }}>{intradayError}</div>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <input
            value={newSymbol}
            onChange={(e) => setNewSymbol(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
            placeholder="Symbol (e.g. AAPL)"
            style={{ padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14, fontFamily: "monospace", width: 200 }}
          />
          <button
            onClick={handleSubscribe}
            style={{ background: "#3b82f6", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 14 }}
          >
            Subscribe
          </button>
        </div>

        {subscriptions.length > 0 ? (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 8 }}>
              Persistent Subscriptions ({subscriptions.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {subscriptions.map((sym) => (
                <div
                  key={sym}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: selectedSymbol === sym ? "#eff6ff" : "#f8fafc",
                    border: `1px solid ${selectedSymbol === sym ? "#bfdbfe" : "#e2e8f0"}`,
                    borderRadius: 6,
                    padding: "6px 12px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSelectSymbol(sym)}
                >
                  <span style={{ flex: 1, fontSize: 14, fontFamily: "monospace", fontWeight: selectedSymbol === sym ? 700 : 400, color: selectedSymbol === sym ? "#1d4ed8" : "#334155" }}>
                    {sym}
                  </span>
                  {selectedSymbol === sym && <span style={{ fontSize: 11, color: "#3b82f6", marginRight: 4 }}>Viewing</span>}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleUnsubscribe(sym); }}
                    title={`Remove ${sym}`}
                    style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 4, cursor: "pointer", color: "#ef4444", fontSize: 12, padding: "2px 6px", lineHeight: 1 }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ color: "#94a3b8", fontSize: 13 }}>No subscriptions yet. Add a symbol above to start capturing real-time data.</div>
        )}
      </Section>

      {/* ── Intraday chart ────────────────────────────────────────── */}
      {selectedSymbol && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 20, marginBottom: 16, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, color: "#1e293b" }}>{selectedSymbol} — 1-Minute Bars</h3>
            {quote && (
              <div style={{ display: "flex", gap: 16, fontSize: 13, marginLeft: "auto", flexWrap: "wrap" }}>
                <div>
                  <span style={{ color: "#64748b" }}>Price: </span>
                  <strong>{quote.price.toFixed(2)}</strong>
                </div>
                <div style={{ color: quote.change >= 0 ? "#16a34a" : "#dc2626" }}>
                  {quote.change >= 0 ? "▲" : "▼"} {Math.abs(quote.change).toFixed(2)} ({quote.change_pct.toFixed(2)}%)
                </div>
                <div>
                  <span style={{ color: "#64748b" }}>H/L: </span>
                  {quote.high.toFixed(2)} / {quote.low.toFixed(2)}
                </div>
              </div>
            )}
          </div>

          {loadingBars && <p>Loading…</p>}

          {!loadingBars && formatted.length === 0 && (
            <p style={{ color: "#94a3b8" }}>No intraday bars yet. Subscribe to this symbol to start capturing data.</p>
          )}

          {!loadingBars && formatted.length > 0 && (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={formatted} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="timeLabel" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} width={80} domain={yDomain} tickFormatter={(v: number) => v.toFixed(2)} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.timeLabel}</div>
                          <div>O: <strong>{d.open?.toFixed(4) ?? "—"}</strong></div>
                          <div>H: <strong style={{ color: "#16a34a" }}>{d.high?.toFixed(4) ?? "—"}</strong></div>
                          <div>L: <strong style={{ color: "#dc2626" }}>{d.low?.toFixed(4) ?? "—"}</strong></div>
                          <div>C: <strong style={{ color: "#3b82f6" }}>{d.close?.toFixed(4) ?? "—"}</strong></div>
                          {d.volume != null && <div style={{ color: "#64748b" }}>Vol: {d.volume.toLocaleString()}</div>}
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="close" shape={<CandlestickBar />} isAnimationActive={false} />
                  <Line type="monotone" dataKey="close" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="Close" connectNulls isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>

              <div style={{ overflowX: "auto", marginTop: 20 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      {["Time (UTC)", "Open", "High", "Low", "Close", "Volume", "Ticks"].map((h) => (
                        <th key={h} style={{ padding: "6px 12px", color: "#64748b", fontWeight: 600, textAlign: "left" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...formatted].reverse().slice(0, 50).map((b) => (
                      <tr key={b.id} style={{ borderBottom: "1px solid #f8fafc" }}>
                        <td style={{ padding: "5px 12px", fontFamily: "monospace" }}>{format(new Date(b.timestamp), "yyyy-MM-dd HH:mm")}</td>
                        <td style={{ padding: "5px 12px", textAlign: "right" }}>{b.open?.toFixed(4) ?? "—"}</td>
                        <td style={{ padding: "5px 12px", textAlign: "right" }}>{b.high?.toFixed(4) ?? "—"}</td>
                        <td style={{ padding: "5px 12px", textAlign: "right" }}>{b.low?.toFixed(4) ?? "—"}</td>
                        <td style={{ padding: "5px 12px", textAlign: "right", fontWeight: 600 }}>{b.close?.toFixed(4) ?? "—"}</td>
                        <td style={{ padding: "5px 12px", textAlign: "right" }}>{b.volume?.toLocaleString() ?? "—"}</td>
                        <td style={{ padding: "5px 12px", textAlign: "right" }}>{b.trade_count ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
